import { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { useClinicType } from '@/hooks/useClinicType';
import { useActiveClinic } from '@/lib/activeClinic';
import { useDentalRealtime } from '@/hooks/useDental';
import { PatientStrip } from '@/components/dental/PatientStrip';
import { DentalSubNav, type DentalView } from '@/components/dental/DentalSubNav';
import { ShortcutModeProvider } from '@/components/dental/shortcuts/ShortcutModeContext';
import { PlanRoute } from '@/components/dental/plan/PlanRoute';
import { SessionRoute } from '@/components/dental/session/SessionRoute';
import { HistoryRoute } from '@/components/dental/history/HistoryRoute';
import { ImagingRoute } from '@/components/dental/imaging/ImagingRoute';
import { DentalOverlayProvider } from '@/components/dental/imaging/DentalOverlayContext';
import { PerioRoute } from '@/components/dental/perio/PerioPanel';
import { PageLayout } from '@/components/ui/PageLayout';

// ---------------------------------------------------------------------------
// Full-page spinner
// ---------------------------------------------------------------------------
function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-ds-teal" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DentalModulePage
// ---------------------------------------------------------------------------
const VALID_VIEWS: DentalView[] = ['plan', 'session', 'history', 'imaging', 'perio'];

function isValidView(v: string | null): v is DentalView {
  return VALID_VIEWS.includes(v as DentalView);
}

// Inner component — needs ShortcutModeProvider to be in the tree
function DentalModulePageInner() {
  const { t } = useTranslation();
  const { id: patientId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDentist, isLoading: clinicLoading } = useClinicType();
  const { activeClinicId } = useActiveClinic();

  const rawView = searchParams.get('view');
  const view: DentalView = isValidView(rawView) ? rawView : 'plan';

  // Single realtime subscription for the entire module — invalidates queries
  // for tooth-status, treatments, and plans. Hoisted here so we only open ONE
  // Supabase channel per module mount regardless of how many child panels are
  // visible (otherwise multiple `.channel(name)` calls collide; see hook for
  // full rationale).
  useDentalRealtime(patientId ?? null);

  useEffect(() => {
    if (!clinicLoading && !isDentist && patientId) {
      toast.error(t('dental.module.noDentistClinicError'));
      navigate(`/patients/${patientId}`, { replace: true });
    }
  }, [clinicLoading, isDentist, patientId, navigate, t]);

  useEffect(() => {
    if (!isValidView(rawView)) {
      setSearchParams({ view: 'plan' }, { replace: true });
    }
  }, [rawView, setSearchParams]);

  if (clinicLoading) {
    return <FullPageSpinner />;
  }

  if (!patientId) {
    return null;
  }

  const handleViewChange = (newView: DentalView) => {
    setSearchParams({ view: newView });
  };

  return (
    <PageLayout fill>
    <DentalOverlayProvider>
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <PatientStrip patientId={patientId} />
        <DentalSubNav activeView={view} onChange={handleViewChange} />

        <div className="flex-1 overflow-hidden bg-ds-bg">
          {view === 'plan' && activeClinicId && (
            <PlanRoute patientId={patientId} clinicId={activeClinicId} />
          )}

          {view === 'session' && activeClinicId && (
            <SessionRoute patientId={patientId} clinicId={activeClinicId} />
          )}

          {view === 'history' && activeClinicId && (
            <HistoryRoute patientId={patientId} clinicId={activeClinicId} />
          )}

          {view === 'imaging' && activeClinicId && (
            <ImagingRoute patientId={patientId} clinicId={activeClinicId} />
          )}

          {view === 'perio' && activeClinicId && (
            <PerioRoute patientId={patientId} clinicId={activeClinicId} />
          )}
        </div>
      </div>
    </DentalOverlayProvider>
    </PageLayout>
  );
}

export function DentalModulePage() {
  return (
    <ShortcutModeProvider>
      <DentalModulePageInner />
    </ShortcutModeProvider>
  );
}
